import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiClientError } from '@/lib/api-client';
import {
  approveMaster,
  activateMaster,
  createMaster,
  listMaster,
  proposeMasterChange,
  purgeMaster,
  rejectMaster,
  retireMaster,
  updateMasterDirect,
  type MasterListParams,
  type MasterListResult,
  type ProposeChangeResult,
} from '@/lib/api-masters';
import type { Destination, DestinationCreateInput } from './types';

const KEY = 'destinations' as const;

export const useDestinationsList = (params: MasterListParams = {}) => {
  return useQuery<MasterListResult<Destination>, ApiClientError>({
    queryKey: [KEY, 'list', params],
    queryFn: () => listMaster<Destination>('destinations', params),
    placeholderData: (prev) => prev,
  });
};

export const useCreateDestination = () => {
  const qc = useQueryClient();
  return useMutation<Destination, ApiClientError, DestinationCreateInput>({
    mutationFn: (body) => createMaster<Destination, DestinationCreateInput>('destinations', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useApproveDestination = () => {
  const qc = useQueryClient();
  return useMutation<Destination, ApiClientError, { id: string; reason: string }>({
    mutationFn: ({ id, reason }) => approveMaster<Destination>('destinations', id, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useRejectDestination = () => {
  const qc = useQueryClient();
  return useMutation<Destination, ApiClientError, { id: string; reason: string }>({
    mutationFn: ({ id, reason }) => rejectMaster<Destination>('destinations', id, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useRetireDestination = () => {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: (id) => retireMaster('destinations', id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useActivateDestination = () => {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: (id) => activateMaster('destinations', id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const usePurgeDestination = () => {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: (id) => purgeMaster('destinations', id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

/** Manager-only direct edit. `PUT /api/masters/destinations/:id`. */
export const useUpdateDestinationDirect = () => {
  const qc = useQueryClient();
  return useMutation<
    Destination,
    ApiClientError,
    { id: string; body: Partial<DestinationCreateInput> }
  >({
    mutationFn: ({ id, body }) =>
      updateMasterDirect<Destination, Partial<DestinationCreateInput>>('destinations', id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

/** Supervisor+ change-proposal. */
export const useProposeDestinationChange = () => {
  const qc = useQueryClient();
  return useMutation<
    ProposeChangeResult,
    ApiClientError,
    { id: string; body: { proposed: Partial<DestinationCreateInput>; reason: string } }
  >({
    mutationFn: ({ id, body }) =>
      proposeMasterChange<Partial<DestinationCreateInput>>('destinations', id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};
